import React from "react";
import { Dropdown, Input, Spin } from "antd";
import { DownOutlined, LeftOutlined, MailOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import styled, { createGlobalStyle } from "styled-components";
import { POPUP_KEEP_IN_VIEWPORT } from "@/shared/utils/popupOverflow";
import type { CampaignEngagementTerms, EngagementCreator } from "@/api/engagementTerms";
import type { Market } from "@/shared/types/campaign/types";
import { AppSelect } from "@/shared/components";
import { Icon } from "@/shared/components/Icon/Icon";
import { getCountryFlag, getCountryFlagUrl } from "@/shared/utils/formatters";
import { TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

const MarketPane = styled.aside`
	min-height: 0;
	background: #f3f5ff;
	border: 1px solid #cdd4f6;
	border-radius: 16px;
	padding: 24px;
	overflow-y: auto;

	${mediaPhone`
		padding: 16px;
	`}
`;
const PaneTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #222;
`;
const PaneSubtitle = styled.div`
	margin-top: 4px;
	font-size: 12px;
	color: rgba(34, 34, 34, 0.6);
`;
const MarketControl = styled.div`
	margin-top: 8px;
`;
const MarketButton = styled.div<{ $active: boolean }>`
	display: flex;
	align-items: center;
	width: 100%;
	height: 48px;
	box-sizing: border-box;
	padding: 4px;
	border: 1px solid ${(props) => (props.$active ? "#f72586" : "#cdd4f6")};
	border-radius: 8px;
	background: ${(props) => (props.$active ? "#ee0570" : "#fff")};
	box-shadow: ${(props) => (props.$active ? "0 4px 6px rgba(247, 37, 134, 0.24)" : "none")};
	color: ${(props) => (props.$active ? "#fff" : "#303030")};
	cursor: pointer;
`;
const MarketButtonLabel = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	flex: 1;
	padding: 0 8px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font: 600 14px/24px var(--font-family-app);
`;
const MarketButtonCountry = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
const MarketChevronButton = styled.button<{ $active: boolean }>`
	display: grid;
	place-items: center;
	width: 40px;
	height: 40px;
	padding: 0;
	border: 0;
	border-radius: 4px;
	background: ${(props) => (props.$active ? "#de0468" : "#f3f5ff")};
	color: inherit;
	cursor: pointer;
`;
const MarketSelectWrap = styled.div<{ $active: boolean }>`
	position: relative;
	width: 100%;
	height: 48px;

	.ant-select {
		display: block;
		width: 100%;
		height: 48px;
	}
	.ant-select-selector {
		height: 48px !important;
		display: flex !important;
		align-items: center !important;
		padding: 0 52px 0 44px !important;
		border: 1px solid ${(props) => (props.$active ? "#f72586" : "#cdd4f6")} !important;
		border-radius: 8px !important;
		background: ${(props) => (props.$active ? "#ee0570" : "#fff")} !important;
		box-shadow: ${(props) => (props.$active ? "0 4px 6px rgba(247, 37, 134, 0.24)" : "none")} !important;
	}
	.ant-select-selection-item {
		display: flex !important;
		align-items: center !important;
		color: ${(props) => (props.$active ? "#fff" : "#303030")} !important;
		font: 600 14px/24px var(--font-family-app) !important;
	}
	.app-select-control .ant-select-arrow {
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		top: 4px !important;
		right: 4px !important;
		width: 40px;
		height: 40px;
		margin-top: 0 !important;
		color: ${(props) => (props.$active ? "#fff" : "#303030")};
		transform: none !important;
	}

	.app-select-control .ant-select-arrow > span,
	.app-select-control .ant-select-arrow .anticon {
		display: grid !important;
		place-items: center !important;
		width: 40px;
		height: 40px;
		margin: 0 !important;
		line-height: 1;
	}
`;
const Flag = styled.img`
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
`;
const FlagFallback = styled.span`
	display: grid;
	place-items: center;
	width: 24px;
	height: 24px;
	font-size: 19px;
`;
const MarketSelectFlag = styled.span`
	position: absolute;
	top: 12px;
	left: 12px;
	z-index: 2;
	display: grid;
	place-items: center;
	width: 24px;
	height: 24px;
	pointer-events: none;
`;
const ActiveChevron = styled.span<{ $active: boolean }>`
	display: grid;
	place-items: center;
	width: 40px;
	height: 40px;
	border-radius: 4px;
	background: ${(props) => (props.$active ? "#de0468" : "#f3f5ff")};
`;
const PaneDivider = styled.div`
	height: 1px;
	margin: 16px 0;
	background: #bdc6f2;
`;
const EmptyCreators = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	padding: 16px 0;
	color: #9b9b9b;
	font-size: 14px;
	font-weight: 600;
	opacity: 0.68;
`;
const CreatorSearch = styled(Input)`
	&.ant-input-affix-wrapper {
		height: 32px;
		border-color: #cdd4f6;
		border-radius: 8px;
		background: #fff;
	}

	${mediaPhone`
		&.ant-input-affix-wrapper {
			height: ${TAP_TARGET}px;
		}

		input {
			font-size: 16px;
		}
	`}
`;
const CreatorFilters = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin: 8px 0 12px;
`;
const CreatorFilter = styled.button<{ $active?: boolean }>`
	padding: 3px 9px;
	border: 1px solid ${(props) => (props.$active ? "#5b21c8" : "#d5d5d5")};
	border-radius: 20px;
	background: ${(props) => (props.$active ? "#5b21c8" : "#fff")};
	color: ${(props) => (props.$active ? "#fff" : "#555")};
	font-size: 11px;
	white-space: nowrap;
	cursor: pointer;

	${mediaPhone`
		min-height: 32px;
		padding: 4px 12px;
		font-size: 12px;
	`}
`;
const CreatorList = styled.div`
	display: grid;
	gap: 8px;
`;
const CreatorRow = styled.button<{ $selected: boolean }>`
	content-visibility: auto;
	contain-intrinsic-size: 40px;
	display: grid;
	grid-template-columns: 28px minmax(0, 1fr) auto;
	align-items: center;
	gap: 8px;
	width: 100%;
	height: 40px;
	padding: 5px 8px;
	border: 1px solid ${(props) => (props.$selected ? "#ef0a71" : "#d9def8")};
	border-radius: 8px;
	background: ${(props) => (props.$selected ? "#ef0a71" : "#fff")};
	color: ${(props) => (props.$selected ? "#fff" : "#454545")};
	font: 500 12px var(--font-family-app);
	cursor: pointer;
	text-align: left;

	${mediaPhone`
		height: 52px;
		font-size: 14px;
	`}
	${pressedTint}
`;
export const CreatorAvatar = styled.img`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	object-fit: cover;
`;
const CREATOR_AVATAR_FALLBACK = "/assets/creator-onboarding/default-avatar.svg";
const useDefaultAvatar = (event: React.SyntheticEvent<HTMLImageElement>) => {
	const image = event.currentTarget;
	if (image.src.endsWith(CREATOR_AVATAR_FALLBACK)) return;
	image.removeAttribute("srcset");
	image.src = CREATOR_AVATAR_FALLBACK;
};
const CreatorRowName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
const CreatorStatus = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 7px;
`;
const CreatorVersion = styled.span<{ $selected: boolean }>`
	padding: 3px 5px;
	border-radius: 4px;
	background: ${(props) => (props.$selected ? "rgba(255,255,255,0.18)" : "#f3f5ff")};
	color: inherit;
	font-size: 10px;
	font-weight: 600;
`;
const CreatorStateIcon = styled.span<{ $tone: "locked" | "custom"; $selected: boolean }>`
	display: grid;
	place-items: center;
	color: ${(props) => (props.$selected ? "#fff" : props.$tone === "locked" ? "#ff405b" : "#e49a00")};
	font-size: 17px;
`;

interface Props {
	markets: Market[];
	selectedCountry: string;
	onCountryChange: (country: string) => void;
	creators: EngagementCreator[];
	filteredCreators: EngagementCreator[];
	selectedId: string | null;
	onCreatorSelect: (assignmentId: string) => void;
	onMarketSelect: () => void;
	search: string;
	onSearchChange: (value: string) => void;
	loading: boolean;
	marketTermsSaved: boolean;
}

interface CreatorRowsProps {
	creators: EngagementCreator[];
	selectedId: string | null;
	onCreatorSelect: (assignmentId: string) => void;
}

type CreatorFilterValue = "all" | "synced" | "customized" | "sent" | "accepted";

const CreatorRows = React.memo<CreatorRowsProps>(({ creators, selectedId, onCreatorSelect }) => (
	<CreatorList>
		{creators.map((creator) => {
			const selected = selectedId === creator.assignmentId;
			return (
				<CreatorRow
					key={creator.assignmentId}
					$selected={selected}
					onClick={() => onCreatorSelect(creator.assignmentId)}
				>
					<CreatorAvatar
						src={creator.avatarUrl || CREATOR_AVATAR_FALLBACK}
						alt={creator.name}
						onError={useDefaultAvatar}
					/>
					<CreatorRowName>{creator.name}</CreatorRowName>
					<CreatorStatus>
						{creator.latestVersionNumber && (
							<CreatorVersion $selected={selected}>V{creator.latestVersionNumber}</CreatorVersion>
						)}
						{(creator.invitationStatus === "sent" || creator.invitationStatus === "accepted") && (
							<CreatorStateIcon $tone="locked" $selected={selected}>
								<Icon src="/icon/cet-locked.svg" size={15} />
							</CreatorStateIcon>
						)}
						{creator.isCustomized && (
							<CreatorStateIcon $tone="custom" $selected={selected}>
								<Icon src="/icon/cet-custom.svg" size={15} />
							</CreatorStateIcon>
						)}
						<RightOutlined />
					</CreatorStatus>
				</CreatorRow>
			);
		})}
	</CreatorList>
));
CreatorRows.displayName = "CreatorRows";

const EngagementCreatorsPanelComponent: React.FC<Props> = ({
	markets,
	selectedCountry,
	onCountryChange,
	creators,
	filteredCreators,
	selectedId,
	onCreatorSelect,
	onMarketSelect,
	search,
	onSearchChange,
	loading,
	marketTermsSaved,
}) => {
	const [marketSelectOpen, setMarketSelectOpen] = React.useState(false);
	const [activeFilter, setActiveFilter] = React.useState<CreatorFilterValue>("all");
	const counts = React.useMemo(
		() => ({
			all: creators.length,
			synced: creators.filter((creator) => !creator.isCustomized).length,
			customized: creators.filter((creator) => creator.isCustomized).length,
			sent: creators.filter((creator) => creator.invitationStatus === "sent").length,
			accepted: creators.filter((creator) => creator.invitationStatus === "accepted").length,
		}),
		[creators],
	);
	const visibleCreators = React.useMemo(
		() =>
			filteredCreators.filter((creator) => {
				switch (activeFilter) {
					case "synced":
						return !creator.isCustomized;
					case "customized":
						return creator.isCustomized;
					case "sent":
						return creator.invitationStatus === "sent";
					case "accepted":
						return creator.invitationStatus === "accepted";
					default:
						return true;
				}
			}),
		[activeFilter, filteredCreators],
	);
	const filters: Array<{ value: CreatorFilterValue; label: string; count: number }> = [
		{ value: "all", label: "All", count: counts.all },
		{ value: "synced", label: "Synced", count: counts.synced },
		{ value: "customized", label: "Customized", count: counts.customized },
		{ value: "sent", label: "Sent", count: counts.sent },
		{ value: "accepted", label: "Accepted", count: counts.accepted },
	];
	return (
		<MarketPane>
			<PaneTitle>Markets &amp; Creators</PaneTitle>
			<PaneSubtitle>{markets.length} market engagement terms</PaneSubtitle>
			<MarketControl>
				{marketSelectOpen ? (
					<MarketSelectWrap $active={!selectedId}>
						<MarketSelectFlag>
							{getCountryFlagUrl(selectedCountry) ? (
								<Flag src={getCountryFlagUrl(selectedCountry)} alt="" />
							) : (
								<FlagFallback>{getCountryFlag(selectedCountry)}</FlagFallback>
							)}
						</MarketSelectFlag>
						<AppSelect
							autoFocus
							open
							value={selectedCountry || undefined}
							placeholder="Select market"
							onChange={(country) => {
								onCountryChange(country);
								setMarketSelectOpen(false);
							}}
							onOpenChange={(open) => !open && setMarketSelectOpen(false)}
							popupMatchSelectWidth
							suffixIcon={
								<ActiveChevron $active={!selectedId}>
									<DownOutlined />
								</ActiveChevron>
							}
							options={markets.map((market) => ({
								value: market.country || "",
								label: market.country || "",
							}))}
						/>
					</MarketSelectWrap>
				) : (
					<MarketButton
						$active={!selectedId}
						role="button"
						tabIndex={0}
						onClick={onMarketSelect}
						onKeyDown={(event) => {
							if (event.key === " ") {
								event.preventDefault();
								onMarketSelect();
							} else if (event.key === "Enter") {
								onMarketSelect();
							}
						}}
					>
						<MarketButtonLabel>
							{getCountryFlagUrl(selectedCountry) ? (
								<Flag src={getCountryFlagUrl(selectedCountry)} alt="" />
							) : (
								<FlagFallback>{getCountryFlag(selectedCountry)}</FlagFallback>
							)}
							<MarketButtonCountry>{selectedCountry || "Select market"}</MarketButtonCountry>
						</MarketButtonLabel>
						<MarketChevronButton
							type="button"
							$active={!selectedId}
							aria-label="Choose market"
							onClick={(event) => {
								event.stopPropagation();
								setMarketSelectOpen(true);
							}}
						>
							<DownOutlined />
						</MarketChevronButton>
					</MarketButton>
				)}
			</MarketControl>
			<PaneDivider />
			{loading ? (
				<EmptyCreators>
					<Spin size="small" />
				</EmptyCreators>
			) : !marketTermsSaved ? (
				<EmptyCreators>
					<Icon src="/icon/cet-empty-creator.svg" size={24} preserveOriginalColor />
					<span>Save the market engagement terms to manage creators</span>
				</EmptyCreators>
			) : creators.length ? (
				<>
					<CreatorSearch
						value={search}
						prefix={<SearchOutlined />}
						placeholder="Search creators"
						onChange={(event) => onSearchChange(event.target.value)}
					/>
					<CreatorFilters>
						{filters.map((filter) => (
							<CreatorFilter
								key={filter.value}
								type="button"
								$active={activeFilter === filter.value}
								aria-pressed={activeFilter === filter.value}
								onClick={() => setActiveFilter(filter.value)}
							>
								{filter.label} {String(filter.count).padStart(2, "0")}
							</CreatorFilter>
						))}
					</CreatorFilters>
					<CreatorRows creators={visibleCreators} selectedId={selectedId} onCreatorSelect={onCreatorSelect} />
				</>
			) : (
				<EmptyCreators>
					<Icon src="/icon/cet-empty-creator.svg" size={24} preserveOriginalColor />
					<span>No creators here yet</span>
				</EmptyCreators>
			)}
		</MarketPane>
	);
};

export const EngagementCreatorsPanel = React.memo(EngagementCreatorsPanelComponent);
EngagementCreatorsPanel.displayName = "EngagementCreatorsPanel";

const FormHeader = styled.header`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 24px;
	border-bottom: 1px solid #e6e6e6;

	/* Phones: the creator on one row, the version picker as a full-width row under hairlines. */
	${mediaPhone`
		position: sticky;
		top: 0;
		z-index: 3;
		flex-wrap: wrap;
		gap: 0;
		padding: 0;
		background: #fff;
	`}
`;

/** Phones only: back from the terms form to the market and creator list. */
const BackToList = styled.button`
	display: none;

	${mediaPhone`
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
		border: none;
		border-radius: 50%;
		background: transparent;
		color: #3a0ca3;
		cursor: pointer;
	`}
	${pressedTint}
`;

const HeaderStart = styled.div`
	display: flex;
	align-items: center;
	min-width: 0;

	${mediaPhone`
		flex: 1 1 100%;
		padding: 4px 16px 4px 4px;
	`}
`;
const HeaderIdentity = styled.div`
	display: flex;
	min-width: 0;
	align-items: center;
	gap: 12px;
`;
const LargeFlag = styled(Flag)`
	width: 40px;
	height: 40px;
`;
const FormTitle = styled.div`
	font-size: 18px;
	font-weight: 700;
	color: #222;
`;
const FormMarket = styled.div`
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	color: rgba(34, 34, 34, 0.6);
`;
const HeaderCreator = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;

	${mediaPhone`
		flex: 1 1 auto;
		gap: 8px;
	`}
`;
const HeaderCreatorAvatar = styled(CreatorAvatar)`
	width: 40px;
	height: 40px;

	${mediaPhone`
		width: 32px;
		height: 32px;
	`}
`;
const HeaderCreatorText = styled.div`
	display: grid;
	min-width: 0;

	${mediaPhone`
		flex: 1 1 auto;
	`}
`;
const HeaderCreatorName = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #303030;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	${mediaPhone`
		font-size: 16px;
	`}
`;
const HeaderCreatorHandle = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #707070;

	/* The email is a mailto link that looks like the plain text it replaced. */
	a {
		color: inherit;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	/* Phones: the email stays under the name on the same row, and truncates instead of wrapping the header. */
	${mediaPhone`
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	`}
`;
const VersionTrigger = styled.button`
	display: flex;
	width: 172px;
	height: 38px;
	align-items: center;
	justify-content: space-between;
	padding: 0 12px;
	border: 1px solid rgba(58, 12, 163, 0.41);
	border-radius: 7px;
	background: #fff;
	color: #000;
	font: 600 13.5px var(--font-family-app);
	cursor: pointer;

	.anticon {
		font-size: 12px;
	}

	${mediaPhone`
		flex: 1 1 100%;
		width: 100%;
		height: ${TAP_TARGET}px;
		padding: 0 16px;
		border: 0;
		border-top: 1px solid #e6e6e6;
		border-radius: 0;
		background: transparent;
		font-size: 14px;
	`}
	${pressedTint}
`;
const VersionMenuLabel = styled.span`
	display: flex;
	width: 190px;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	color: #000;
	font: 600 13.5px var(--font-family-app);

	em {
		font-size: 10px;
		font-weight: 400;
	}
`;
const LatestSuffix = styled.span`
	font-weight: 400;
`;
const VersionDropdownStyles = createGlobalStyle`
	.cet-version-dropdown.ant-dropdown {
		width: 246px;
		padding: 12px 0;
	}

	.cet-version-dropdown .ant-dropdown-menu {
		padding: 6px;
		border: 1px solid #d9d4ed;
		border-radius: 10px;
		box-shadow: 0 8px 24px rgba(21, 89, 165, 0.16);
	}

	.cet-version-dropdown .ant-dropdown-menu-item {
		min-height: 38px;
		padding: 8px 12px;
		border-radius: 6px;
	}

	.cet-version-dropdown .ant-dropdown-menu-item-selected {
		background: #ede8fa;
		color: #000;
	}
`;
const HeaderStatusBadge = styled.span<{ $tone: "sent" | "accepted" | "locked" }>`
	display: inline-flex;
	align-self: flex-start;
	height: 24px;
	align-items: center;
	gap: 5px;
	padding: 3px 8px;
	border: 1px solid ${(props) => (props.$tone === "locked" ? "#ff5a6f" : "transparent")};
	border-radius: 999px;
	background: ${(props) => (props.$tone === "sent" ? "#e7f9ff" : props.$tone === "accepted" ? "#eaf9dc" : "#fff1f3")};
	color: ${(props) => (props.$tone === "sent" ? "#00a9d6" : props.$tone === "accepted" ? "#43851c" : "#ff405b")};
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;

	${mediaPhone`
		align-self: center;
		flex-shrink: 0;
	`}
`;

export const EngagementTermsHeader: React.FC<{
	country: string;
	creator: EngagementCreator | null;
	versions?: CampaignEngagementTerms[];
	selectedVersionId?: string | null;
	onVersionSelect?: (termsId: string) => void;
	/** Phones: return to the creator list. */
	onBack?: () => void;
}> = ({ country, creator, versions = [], selectedVersionId, onVersionSelect, onBack }) => {
	const flagUrl = getCountryFlagUrl(country);
	const selectedVersion = versions.find((version) => version.id === selectedVersionId) || versions[0];
	return (
		<FormHeader>
			<VersionDropdownStyles />
			{creator ? (
				<>
					<HeaderStart>
						{onBack && (
							<BackToList type="button" aria-label="Back to creators" onClick={onBack}>
								<LeftOutlined />
							</BackToList>
						)}
						<HeaderCreator>
							<HeaderCreatorAvatar
								src={creator.avatarUrl || CREATOR_AVATAR_FALLBACK}
								alt={creator.name}
								onError={useDefaultAvatar}
							/>
							<HeaderCreatorText>
								<HeaderCreatorName>{creator.name}</HeaderCreatorName>
								<HeaderCreatorHandle>
									{creator.email ? (
										<a href={`mailto:${creator.email}`}>{creator.email}</a>
									) : (
										`@${creator.handle?.replace(/^@/, "")}`
									)}
								</HeaderCreatorHandle>
							</HeaderCreatorText>
							{selectedVersion?.responseStatus === "accepted" && (
								<HeaderStatusBadge $tone="locked">
									<Icon src="/icon/cet-locked.svg" size={16} /> Locked
								</HeaderStatusBadge>
							)}
							{selectedVersion?.responseStatus === "pending" && (
								<HeaderStatusBadge $tone="sent">
									<MailOutlined /> Sent to Creator
								</HeaderStatusBadge>
							)}
						</HeaderCreator>
					</HeaderStart>
					{versions.length > 0 && selectedVersion && (
						<Dropdown
							autoAdjustOverflow={POPUP_KEEP_IN_VIEWPORT}
							trigger={["click"]}
							overlayClassName="cet-version-dropdown"
							menu={{
								selectedKeys: [selectedVersion.id],
								items: versions.map((version) => ({
									key: version.id,
									label: (
										<VersionMenuLabel>
											<span>Version {version.versionNumber}</span>
											<em>{dayjs(version.updatedAt).format("MMMM D, YYYY")}</em>
										</VersionMenuLabel>
									),
								})),
								onClick: ({ key }) => onVersionSelect?.(String(key)),
							}}
						>
							<VersionTrigger type="button">
								<span>
									Version {selectedVersion.versionNumber}
									{selectedVersion.id === versions[0]?.id && <LatestSuffix> (latest)</LatestSuffix>}
								</span>
								<DownOutlined />
							</VersionTrigger>
						</Dropdown>
					)}
				</>
			) : (
				<HeaderStart>
					{onBack && (
						<BackToList type="button" aria-label="Back to creators" onClick={onBack}>
							<LeftOutlined />
						</BackToList>
					)}
					<HeaderIdentity>
						{flagUrl ? (
							<LargeFlag src={flagUrl} alt="" />
						) : (
							<FlagFallback>{getCountryFlag(country)}</FlagFallback>
						)}
						<div>
							<FormTitle>Engagement Terms</FormTitle>
							<FormMarket>{country}</FormMarket>
						</div>
					</HeaderIdentity>
				</HeaderStart>
			)}
		</FormHeader>
	);
};
